import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import JobIcon from "components/job-icon/JobIcon";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import RelicCheckbox from "components/relic-checkbox/RelicCheckbox";
import { trials } from "data-files/duties";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { GameData, Job, LinkType, RelicData, View } from "enums";
import log from "logger";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import { Character } from "types/data";
import { Info, Obtained as ItemObtained } from "types/game";
import utils from "util/utils";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponAnimaLuxProps } from "views/relics/weapons/anima/lux/types";
import { TaskInfo } from "views/type";
import "./WeaponAnimaLux.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		options: state.userInfo.options,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showJob: state.userInfo.options.job,
		animaLuxStepJob: state.userInfo.options.relics.animaLuxStepJob,
	};
};

class WeaponAnimaLux extends Component<ViewWeaponAnimaLuxProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_ANIMA,
		part: RelicData.Part.WEAPON_LUX,
		steps: [RelicData.Step.BFF],
		stepPlaceholders: { [RelicData.Step.BFF]: { QUEST: GameData.Quest.BEST_FRIENDS_FOREVER } },
		next: null,
		previous: {
			part: RelicData.Part.WEAPON_COMPLETE,
			view: View.RELIC_WEAPONS_ANIMA_COMPLETE,
		},
	};

	constructor(props:ViewWeaponAnimaLuxProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);
		const stepJob = this.props.animaLuxStepJob;
		const taskInfo = this.getTasksInfo(RelicData.Step.BFF, character, stepJob);

		return (
			<div id="weapon-anima-lux" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("The %PART% upgrade follows on from %PREVIOUSPART%", { PART: this.relicInfo.part, PREVIOUSPART: this.relicInfo.previous ? this.relicInfo.previous.part : "Unknown"})}</p>
					<p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={quests[GameData.Quest.BEST_FRIENDS_FOREVER].type} text={quests[GameData.Quest.BEST_FRIENDS_FOREVER].name} lodestoneID={quests[GameData.Quest.BEST_FRIENDS_FOREVER].lodestoneID} /></p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.BFF} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.BFF]} id={RelicData.Step.BFF} stepComplete={completeSteps[RelicData.Step.BFF]}>
						<div className="job-select">
							<FormComponents.Label
								hide={false}
								translate={true}
								text="Select job to track"
								position="left"
								alignment="center"
							/>
							{this.relicInfo.jobs.map((job) => {
								if(this.props.showJob !== null && this.props.showJob !== job){ return null; }
								const weaponName = manager.relics.getRelicNameKey(job, this.relicInfo.part);
								return <JobIcon key={`${job}`} job={job} tooltip={weaponName} selected={job === stepJob} onClick={() => { this.selectJob(job); }} />;
							})}
						</div>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Type")}</th><th>{t.t("Target")}</th><th>{t.t("Quantity")}</th><th>{t.t("Done")}</th></tr></thead>
								<tbody>
									{
										taskInfo.map((thisTask) => {
											const thisComplete = thisTask.complete;
											if((this.props.hideCompletedSteps || this.props.hideCompletedTasks) && thisComplete){ return null; }

											let thisObtained:ItemObtained.Method = {};
											let thisTarget:Info.Trial|Info.Item|null = null;

											if(utils.guards.isEnumValue(GameData.Trial, thisTask.target)){
												thisTarget = trials[thisTask.target];
												thisObtained.duties = { trials: [thisTask.target]};
											}else if(utils.guards.isEnumValue(GameData.Item, thisTask.target)){
												thisTarget = items[thisTask.target];
												thisObtained = thisTarget.obtained;
											}
											if(thisTarget === null){ log.error("Wrong info set for this task"); return null; }

											return <tr key={`${RelicData.Step.BFF}-${thisTask.task}`}>
												<td><Obtained obtained={thisObtained} qtyNeeded={1} /></td>
												<td>{t.t(thisTarget.name)}</td>
												<td>1</td>
												<td><RelicCheckbox character={character} job={stepJob} relicInfo={this.relicInfo} step={RelicData.Step.BFF} task={thisTask.task} /></td>
											</tr>;
										})
									}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.BFF)
											? <tr className="all-complete"><td colSpan={4}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }
		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
			{ type: quests[GameData.Quest.BODY_SOUL].type, text: GameData.Quest.BODY_SOUL, lodestoneID: quests[GameData.Quest.BODY_SOUL].lodestoneID },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.ARDASHIR];
	}

	private getTasksInfo(step:RelicData.Step, character:Character, job:Job):TaskInfo[]{
		return [
			{ task: RelicData.Task.TASK_1, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_1, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_1) },
			{ task: RelicData.Task.TASK_2, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_2, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_2) },
			{ task: RelicData.Task.TASK_3, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_3, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_3) },
			{ task: RelicData.Task.TASK_4, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_4, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_4) },
			{ task: RelicData.Task.TASK_5, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_5, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_5) },
			{ task: RelicData.Task.TASK_6, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_6, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_6) },
			{ task: RelicData.Task.TASK_7, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_7, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_7) },
			{ task: RelicData.Task.TASK_8, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_8, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_8) },
			{ task: RelicData.Task.TASK_9, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_9, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_9) },
			{ task: RelicData.Task.TASK_10, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_10, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_10) },
			{ task: RelicData.Task.TASK_11, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_11, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_11) },
			{ task: RelicData.Task.TASK_12, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_12, job), target: manager.relics.getTaskDuty(step, RelicData.Task.TASK_12) },
			{ task: RelicData.Task.TASK_13, complete: character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, step, RelicData.Task.TASK_13, job), target: manager.relics.getTaskItem(step, RelicData.Task.TASK_13, job) },
		];
	}

	private selectJob(job:Job){ // Event
		const options = this.props.options;
		options.relics = {
			...options.relics,
			animaLuxStepJob: utils.guards.isEnumValue(Job, job) ? job : Job.PLD,
		};
		manager.data.saveOptions(options, true);
	}
}
export default connect(mapStateToProps)(WeaponAnimaLux);
